import React, { useEffect, useState } from 'react';
import type { BreakpointDef } from '../Config/config';
import { useSimulatedBreakpoint } from './hooks/useBreakpoint';

export const GridOverlay: React.FC<{
  breakpoints: BreakpointDef[];
  showColumns?: boolean;
  showRows?: boolean;
  color?: string;
  opacity?: number;
  gap?: number;
  rowGap?: number;
  rowHeight?: number;
  gridRef?: React.RefObject<HTMLDivElement | null>;
  interactiveRows?: number;
  isSelecting?: boolean;
  simulatedWidth?: number;
}> = ({ breakpoints, showColumns = true, showRows = true, color = '#00f', opacity = 0.25, gap = 0, gridRef, interactiveRows = 0, isSelecting = false, simulatedWidth }) => {
  const { currentBreakpoint: bp } = useSimulatedBreakpoint(breakpoints, simulatedWidth);
  const cols = bp.columns;
  
  // Cases carrées : largeur effective / colonnes = taille d'une case
  const effectiveWidth = simulatedWidth || (typeof window !== 'undefined' ? window.innerWidth : 1920);
  const [viewportWidth, setViewportWidth] = useState(effectiveWidth);
  const [gridHeight, setGridHeight] = useState(1080);

  useEffect(() => {
    let timeoutId: number;
    
    const updateSize = () => {
      // Debouncer les mises à jour pour éviter les recalculs trop fréquents
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        setViewportWidth(simulatedWidth || window.innerWidth);
        if (gridRef?.current) {
          // Prendre la hauteur réelle de la grille (scrollHeight inclut le contenu qui dépasse)
          const height = Math.max(gridRef.current.scrollHeight, gridRef.current.offsetHeight);
          setGridHeight(height);
        }
      }, 50); // Petit délai pour éviter les recalculs trop fréquents
    };
    
    updateSize();
    
    // Observer les changements de taille de la grille
    if (gridRef?.current) {
      const resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(gridRef.current);
      
      // Observer les mutations avec debouncing
      const mutationObserver = new MutationObserver(updateSize);
      mutationObserver.observe(gridRef.current, { 
        childList: true, 
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      });
      
      window.addEventListener('resize', updateSize, { passive: true });
      
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        window.removeEventListener('resize', updateSize);
      };
    }
    
    window.addEventListener('resize', updateSize, { passive: true });
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', updateSize);
    };
  }, [gridRef, simulatedWidth]);

  // Mettre à jour la largeur quand la simulation change
  useEffect(() => {
    setViewportWidth(simulatedWidth || window.innerWidth);
  }, [simulatedWidth]);

  // Taille d'une case carrée
  const cellSize = (viewportWidth - gap * (cols - 1)) / cols;
  
  // Nombre de lignes : utiliser interactiveRows si fourni, sinon calculer
  const baseNumRows = Math.ceil(gridHeight / cellSize);
  const numRows = interactiveRows > 0 ? interactiveRows : baseNumRows;
  
  // Limiter le nombre de lignes pour éviter les performances dégradées
  const maxRows = 50; // Maximum 50 lignes pour éviter le lag
  const limitedNumRows = Math.min(numRows, maxRows);
  
  // Opacité dynamique : très transparent pendant la sélection, normale sinon
  const currentOpacity = isSelecting ? 0.15 : opacity;

  return (
    <svg 
      className="gc-overlay" 
      style={{ 
        pointerEvents: 'none', // CRUCIAL : ne bloque JAMAIS les clics
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: `${limitedNumRows * cellSize}px`,
        zIndex: 1, // Très bas pour ne pas interférer
        overflow: 'visible',
        transition: 'opacity 0.2s ease' // Transition fluide
      }}
    >
      {/* Lignes verticales */}
      {showColumns && Array.from({ length: cols + 1 }, (_, i) => {
        let x = 0;
        if (i === 0) {
          x = 0;
        } else if (i === cols) {
          x = typeof window !== 'undefined' ? (window.innerWidth - gap * (cols - 1)) : 1920;
        } else {
          x = (cellSize * i) + (gap * i);
        }
        
        return (
          <line
            key={`col-${i}`}
            x1={x}
            y1={0}
            x2={x}
            y2={limitedNumRows * cellSize}
            stroke={color}
            strokeWidth="2"
            opacity={currentOpacity}
            style={{ pointerEvents: 'none', transition: 'opacity 0.2s ease' }}
          />
        );
      })}
      
      {/* Lignes horizontales */}
      {showRows && Array.from({ length: limitedNumRows }, (_, i) => {
        const y = (cellSize * i) + (gap * i);
        return (
          <line
            key={`row-${i}`}
            x1={0}
            y1={y}
            x2="100%"
            y2={y}
            stroke={color}
            strokeWidth="2"
            opacity={currentOpacity}
            style={{ pointerEvents: 'none', transition: 'opacity 0.2s ease' }}
          />
        );
      })}
    </svg>
  );
};

export default GridOverlay;